import { ApiError, api } from '@/basedata/apiCall';
import {
  getStoredEventSlug,
  resolveEventSlugFromLocation,
  setStoredEventSlug,
} from '@/helpers/eventSlug';
import {
  getStoredTenantSlug,
  resolveTenantSlugFromLocation,
  setStoredTenantSlug,
} from '@/helpers/tenantSlug';
import type { TenantSlugLocation } from '@/helpers/tenantSlug';
import type { EventContextResponse, TenantBrandingResponse } from '@/types/branding';

export type TenantBrandingState =
  | { status: 'ok'; config: TenantBrandingResponse; event?: EventContextResponse }
  | { status: 'not-found' }
  | { status: 'default' };

let state: TenantBrandingState = { status: 'default' };

/**
 * Resolve o contexto do funil e busca o branding no BFF (uma vez, no bootstrap).
 *
 * Dois modos, nesta ordem:
 * 1. EVENTO — URL foto.identila.com.br/<slug> ou ?evento= (convite por evento):
 *    GET /api/event-context resolve evento + tenant + branding de uma vez. O
 *    slug do tenant retornado é persistido para o restante do funil.
 * 2. TENANT (legado/linha store) — query ?tenant= > subdomínio > path.
 *
 * - Slug da URL sobrescreve o do sessionStorage.
 * - Sem slug nenhum ou slug desconhecido (404 do BFF) → `not-found` (funil 404).
 * - Falha transitória (rede/5xx) → `default` (branding nunca bloqueia o funil;
 *   em modo evento o eventSlug segue no storage e o aceite ainda escopa a sessão).
 */
export async function fetchTenantBranding(
  location: TenantSlugLocation = window.location,
): Promise<TenantBrandingState> {
  const urlEventSlug = resolveEventSlugFromLocation(location);
  if (urlEventSlug !== null) {
    setStoredEventSlug(urlEventSlug);
  }
  const eventSlug = urlEventSlug ?? getStoredEventSlug();

  if (eventSlug !== null) {
    try {
      const event = await api.eventContext.get(eventSlug);
      setStoredTenantSlug(event.tenant.slug);
      state = {
        status: 'ok',
        config: {
          slug: event.tenant.slug,
          displayName: event.tenant.displayName,
          branding: event.branding,
        },
        event,
      };
    } catch (error) {
      state =
        error instanceof ApiError && error.status === 404
          ? { status: 'not-found' }
          : { status: 'default' };
    }
    return state;
  }

  const urlSlug = resolveTenantSlugFromLocation(location);
  if (urlSlug !== null) {
    setStoredTenantSlug(urlSlug);
  }

  const slug = urlSlug ?? getStoredTenantSlug();
  if (slug === null) {
    state = { status: 'not-found' };
    return state;
  }

  try {
    const config = await api.branding.get(slug);
    state = { status: 'ok', config };
  } catch (error) {
    state =
      error instanceof ApiError && error.status === 404
        ? { status: 'not-found' }
        : { status: 'default' };
  }
  return state;
}

export function getTenantBranding(): TenantBrandingState {
  return state;
}

/** Contexto de evento resolvido no bootstrap (null fora do modo evento). */
export function getOnboardingEventContext(): EventContextResponse | null {
  return state.status === 'ok' && state.event ? state.event : null;
}
